import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getSimulation } from "generated/api/simulation";
import { SimulationDTO, SimulationRequest } from "generated/schemas";

import { simulationKeys } from "hooks/api/query-keys";

import {
  SmsSimulatorSimulationAttributes,
  SmsSimulatorSimulationSummaryAttributes,
} from "containers/sms-simulator/sms-simulator.types";

const simulationsApi = getSimulation();

const mapSimulation = (dto: SimulationDTO): SmsSimulatorSimulationAttributes => ({
  id: dto.id,
  clientId: dto.clientId,
  testSmsText: dto.testSmsText,
  systemPrompt: dto.systemPrompt,
  createdDate: dto.createdDate,
  createdBy: dto.createdBy,
  simulationData: (dto.simulationData ?? []).map((conversation) => ({
    persona: {
      id: conversation.persona?.id ?? "",
      name: conversation.persona?.name ?? "",
      grade: conversation.persona?.grade ?? 0,
      profileType: conversation.persona?.profileType ?? "",
      interests: conversation.persona?.interests ?? "",
      lastModifiedDate: "",
    },
    mockMessages: (conversation.mockMessages ?? []).map((message) => ({
      senderId: message.senderId ?? "",
      content: message.content ?? "",
    })),
  })),
});

export const useSimulations = <TData = SmsSimulatorSimulationSummaryAttributes[]>(
  clientId?: string,
  options?: Omit<
    UseQueryOptions<SmsSimulatorSimulationSummaryAttributes[], Error, TData>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: simulationKeys.list(clientId),
    queryFn: () => simulationsApi.getAll1(clientId ? { clientId } : undefined),
    ...options,
  });

/**
 * Imperatively loads a single simulation (with its conversation data) and caches it.
 * Used when selecting a simulation from the "All simulations" list.
 */
export const useFetchSimulation = () => {
  const queryClient = useQueryClient();

  return (id: string) =>
    queryClient.fetchQuery({
      queryKey: simulationKeys.detail(id),
      queryFn: async () => mapSimulation(await simulationsApi.get1({ id })),
    });
};

export const useCreateSimulation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SimulationRequest) =>
      mapSimulation(await simulationsApi.create1(request)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: simulationKeys.lists() });
    },
    onError: () => {
      toast.error({
        data: {
          title: "Failed to create simulation",
          message: "One or more simulations failed to complete. Please try again.",
        },
      });
    },
  });
};
